import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ClipboardCheck, Loader2, Trash2 } from 'lucide-react';
import { useExcluirVisita, useFazendaTalhoes, useVisitasFazenda } from '../api';
import type { VisitaRevisar } from '../schemas';

function fmtData(s: string | null): { dia: string; mes: string; full: string } {
  if (!s) return { dia: '--', mes: '', full: '' };
  try {
    const d = new Date(`${s.slice(0, 10)}T00:00:00`);
    return {
      dia: String(d.getDate()).padStart(2, '0'),
      mes: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
      full: d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
    };
  } catch {
    return { dia: '--', mes: '', full: '' };
  }
}

// Lista de revisão — visitas sincronizadas da fazenda. Cada uma abre a tela de
// revisão (IA sugere/transcreve → agrônomo edita → salvar → relatório).
export function RevisarVisitasView({ imovelId }: { imovelId: number }) {
  const navigate = useNavigate();
  const { data: talhoes } = useFazendaTalhoes(imovelId);
  const { data: visitas, isLoading } = useVisitasFazenda(imovelId);
  const excluir = useExcluirVisita(imovelId);
  const [aExcluir, setAExcluir] = useState<VisitaRevisar | null>(null);
  const fazenda = (talhoes ?? [])[0]?.fazenda ?? 'Fazenda';
  const lista = visitas ?? [];

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">Revisar visitas</h1>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">{fazenda}</span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10">
        {isLoading && <p className="py-8 text-center text-sm text-text-muted">Carregando…</p>}
        {!isLoading && lista.length === 0 && (
          <div className="rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-10 text-center">
            <ClipboardCheck size={26} strokeWidth={1.5} className="mx-auto mb-2 text-text-muted" />
            <p className="text-[0.9rem] font-medium text-text">Nenhuma visita sincronizada</p>
            <p className="mt-1 text-[0.8rem] text-text-2">Sincronize as visitas do dispositivo para revisar.</p>
          </div>
        )}
        <div className="flex flex-col gap-2.5">
          {lista.map((v) => (
            <VisitaRow
              key={v.id}
              v={v}
              onClick={() => void navigate({ to: '/agronomia/visita/$id', params: { id: String(v.id) } })}
              onExcluir={() => setAExcluir(v)}
            />
          ))}
        </div>
      </div>

      {aExcluir && (
        <ConfirmExcluir
          visita={aExcluir}
          pending={excluir.isPending}
          onCancel={() => setAExcluir(null)}
          onConfirm={() =>
            excluir.mutate(aExcluir.id, { onSuccess: () => setAExcluir(null) })
          }
        />
      )}
    </div>
  );
}

function VisitaRow({ v, onClick, onExcluir }: { v: VisitaRevisar; onClick: () => void; onExcluir: () => void }) {
  const { dia, mes } = fmtData(v.data);
  const revisado = v.revisado;
  return (
    <div className="flex items-center gap-1.5 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 pr-1.5">
      <button type="button" onClick={onClick} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left active:scale-[0.99]">
        <span className="flex size-12 shrink-0 flex-col items-center justify-center rounded-[11px] bg-surface3">
          <span className="text-[0.95rem] font-bold leading-none text-text">{dia}</span>
          <span className="text-[0.6rem] font-semibold uppercase text-text-muted">{mes}</span>
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[0.9rem] font-medium text-text">Visita técnica</span>
          <span className="truncate text-[0.8rem] text-text-2">
            {[v.agronomo || 'Agrônomo', `${v.fotos} ${v.fotos === 1 ? 'foto' : 'fotos'}`].join(' · ')}
          </span>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1 pl-2.5 pr-3 text-[0.72rem] font-medium ${
            revisado ? 'bg-[#dcefdf] text-[#1d5a33]' : 'bg-[#f7e6c9] text-[#6a4a17]'
          }`}
        >
          {revisado ? 'Revisado' : 'A revisar'}
        </span>
        <ChevronRight size={16} strokeWidth={2} className="shrink-0 text-[#565350]" />
      </button>
      <button
        type="button"
        onClick={onExcluir}
        aria-label="Excluir visita"
        className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-[#fde3d5] hover:text-[#b3261e] active:scale-95"
      >
        <Trash2 size={17} strokeWidth={1.75} />
      </button>
    </div>
  );
}

function ConfirmExcluir({
  visita,
  pending,
  onCancel,
  onConfirm,
}: {
  visita: VisitaRevisar;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { full } = fmtData(visita.data);
  return (
    <div className="absolute inset-0 z-30 flex items-end justify-center bg-black/40 px-4 pb-6" onClick={onCancel}>
      <div
        className="w-full max-w-sm rounded-[18px] border border-[var(--agro-card-border)] bg-surface p-5 shadow-[var(--shadow-over)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-[#fde3d5]">
          <Trash2 size={20} strokeWidth={1.75} className="text-[#b3261e]" />
        </div>
        <h2 className="text-[1.05rem] font-bold text-text">Excluir visita?</h2>
        <p className="mt-1.5 text-[0.85rem] leading-snug text-text-2">
          {full ? `A visita de ${full} ` : 'Esta visita '}
          será apagada com todas as fotos, anotações e amostras. Não dá para desfazer.
        </p>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-full border-[1.4px] border-[#bdb9b4] py-2.5 text-[0.88rem] font-semibold text-text"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onConfirm}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#b3261e] py-2.5 text-[0.88rem] font-semibold text-white disabled:opacity-60"
          >
            {pending && <Loader2 size={15} strokeWidth={2} className="animate-spin" />}
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}
